import React from "react";
import {
  Flame,
  Trophy,
  Activity,
  Edit3,
  Sparkles,
} from "lucide-react";
import {
  getRank,
  getNextRank,
  xpForLevel,
  PRESET_AVATARS,
} from "../lib/gameLogic";

export default function PlayerProfile({ state, onEditAvatar, onEditName }) {
  const rank = getRank(state.level);
  const nextRank = getNextRank(state.level);
  const xpReq = xpForLevel(state.level);
  const xpPct = Math.min(100, (state.xp / xpReq) * 100);
  const avatar =
    PRESET_AVATARS.find((a) => a.id === state.avatarId) || PRESET_AVATARS[0];

  return (
    <div
      className="relative overflow-hidden border border-white/10 bg-[#0A0D14] p-6 md:p-7 scanline"
      data-testid="player-profile-card"
    >
      <div className="absolute inset-0 pointer-events-none bg-grid opacity-40" />
      <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-purple-600/20 blur-3xl" />
      <div className="absolute -bottom-12 -left-12 h-48 w-48 rounded-full bg-blue-600/20 blur-3xl" />

      <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
        <button
          onClick={onEditAvatar}
          className="relative group shrink-0 mx-auto md:mx-0"
          data-testid="edit-avatar-button"
        >
          <div
            className="absolute -inset-1 opacity-70 blur-md group-hover:opacity-100 transition"
            style={{ background: rank.color }}
          />
          <div
            className="relative h-28 w-28 md:h-36 md:w-36 border-2 overflow-hidden bg-black"
            style={{ borderColor: rank.color }}
          >
            <img
              src={avatar.url}
              alt={avatar.name}
              className="h-full w-full object-cover animate-flicker"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div
              className="absolute bottom-1 left-1 right-1 text-center font-display font-black text-xl tracking-widest"
              style={{ color: rank.color }}
            >
              {rank.code}
            </div>
          </div>
          <div className="mt-2 text-center md:text-left text-[10px] uppercase tracking-[0.3em] text-slate-400 flex items-center justify-center md:justify-start gap-1">
            <Edit3 className="h-3 w-3" /> Change
          </div>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-slate-400">
            <Sparkles className="h-3 w-3 text-purple-400" />
            <span>System Profile</span>
          </div>
          <button
            onClick={onEditName}
            className="mt-1 group flex items-center gap-2 font-display font-black uppercase text-3xl md:text-4xl lg:text-5xl tracking-tight text-white hover:text-purple-300 transition"
            data-testid="edit-name-button"
          >
            <span data-testid="player-username">
              {state.username || "Hunter"}
            </span>
            <Edit3 className="h-4 w-4 opacity-0 group-hover:opacity-60" />
          </button>
          <div className="mt-1 text-sm text-slate-400 font-mono">
            <span style={{ color: rank.color }}>{rank.code}-RANK</span>{" "}
            <span className="text-slate-600">·</span>{" "}
            <span className="uppercase tracking-widest">{rank.name}</span>
          </div>

          {/* Stat row */}
          <div className="mt-5 grid grid-cols-3 gap-3">
            <Stat
              icon={<Trophy className="h-4 w-4 text-blue-400" />}
              label="Level"
              value={state.level}
              testid="stat-level"
              accent="text-blue-300 text-glow-blue"
            />
            <Stat
              icon={<Flame className="h-4 w-4 text-orange-400" />}
              label="Streak"
              value={`${state.streak}d`}
              testid="stat-streak"
              accent="text-orange-300 text-glow-flame"
            />
            <Stat
              icon={<Activity className="h-4 w-4 text-purple-400" />}
              label="Total XP"
              value={state.totalXp.toLocaleString()}
              testid="stat-total-xp"
              accent="text-purple-300 text-glow-purple"
            />
          </div>

          {/* XP bar */}
          <div className="mt-5">
            <div className="flex items-end justify-between text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-1.5">
              <span>EXP · LV {state.level}</span>
              <span className="font-mono text-slate-300">
                {state.xp} / {xpReq}
              </span>
            </div>
            <div
              className="relative h-3 bg-slate-900 border border-white/10 overflow-hidden"
              data-testid="xp-bar"
            >
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"
                style={{ width: `${xpPct}%` }}
              />
              <div
                className="absolute inset-y-0 left-0 shimmer"
                style={{ width: `${xpPct}%` }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-mono">
              <span>{Math.round(xpPct)}% to LV {state.level + 1}</span>
              {nextRank && (
                <span>
                  Next rank:{" "}
                  <span style={{ color: nextRank.color }}>
                    {nextRank.code}
                  </span>{" "}
                  @ LV {nextRank.minLevel}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon, label, value, accent, testid }) => (
  <div className="border border-white/10 bg-black/40 p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-400">
      {icon}
      {label}
    </div>
    <div className={`mt-1 font-mono text-2xl md:text-3xl font-bold ${accent}`}>
      {value}
    </div>
  </div>
);
